import { API_BASE } from '../Constants';

// Espelha api/app/routers/artifacts.py.

export interface ArtifactShare {
  share_id: string;
  access: 'public' | 'private';
  allowed_emails: string[];
  created_at: string | null;
}

export interface ArtifactVersionInfo {
  version: number;
  title?: string;
  reason?: 'create' | 'edit';
  created_at: string | null;
}

export interface ArtifactContent {
  artifact_id: string;
  title: string;
  type: string;
  version: number;
  content: string;
  updated_at: string | null;
}

export interface OwnedArtifact extends ArtifactContent {
  slug: string;
  chat_id: string;
  latest_version: number;
  versions: ArtifactVersionInfo[];
  share: ArtifactShare | null;
}

export class ArtifactApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (token) headers.Authorization = `Auth ${token}`;
  if (init.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_BASE}/api${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.detail || '';
    } catch { /* corpo não-JSON */ }
    throw new ArtifactApiError(res.status, typeof detail === 'string' ? detail : '');
  }
  return res.json();
}

export const getArtifact = (token: string, artifactId: string, version?: number) =>
  request<OwnedArtifact>(
    `/artifacts/${encodeURIComponent(artifactId)}${version ? `?version=${version}` : ''}`,
    {},
    token,
  );

export const shareArtifact = (
  token: string,
  artifactId: string,
  access: 'public' | 'private',
  allowedEmails: string[] = [],
) =>
  request<ArtifactShare>(
    `/artifacts/${encodeURIComponent(artifactId)}/share`,
    { method: 'POST', body: JSON.stringify({ access, allowed_emails: allowedEmails }) },
    token,
  );

export const revokeArtifactShare = (token: string, artifactId: string) =>
  request<{ status: string }>(`/artifacts/${encodeURIComponent(artifactId)}/share`, { method: 'DELETE' }, token);

/** Link do próprio artefato (token opcional: links restritos exigem login). */
export const getSharedArtifact = (shareId: string, token?: string | null) =>
  request<ArtifactContent>(`/artifacts/shared/${encodeURIComponent(shareId)}`, {}, token);

/** Artefato citado numa conversa compartilhada. */
export const getArtifactInSharedConversation = (
  conversationShareId: string,
  artifactId: string,
  version?: number,
  token?: string | null,
) =>
  request<ArtifactContent>(
    `/shared/${encodeURIComponent(conversationShareId)}/artifacts/${encodeURIComponent(artifactId)}${
      version ? `?version=${version}` : ''
    }`,
    {},
    token,
  );

export const artifactShareUrl = (shareId: string) => `${window.location.origin}/artefato/${shareId}`;
